<script setup lang="ts">
import { ref } from 'vue';

import { IconShieldCheck } from '@/components/design/icons';

const emit = defineEmits<{ confirm: [] }>();

const visible = ref(false);

function show(): void {
  visible.value = true;
}

function hide(): void {
  visible.value = false;
}

function handleConfirm(): void {
  hide();
  emit('confirm');
}

defineExpose({ show, hide });
</script>

<template>
  <NModal v-model:show="visible" :mask-closable="true" style="width: 480px">
    <div class="gdrive-notice">
      <!-- ── Header ───────────────────────────────────────────────────── -->
      <div class="gdrive-notice__header">
        <div class="gdrive-notice__logo-wrap">
          <!-- Google multicolor "G" -->
          <svg width="28" height="28" viewBox="0 0 48 48" aria-hidden="true">
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
        </div>

        <div class="gdrive-notice__header-text">
          <p class="gdrive-notice__title">Before you continue to Google</p>
          <p class="gdrive-notice__subtitle">A note about the verification process</p>
        </div>
      </div>

      <!-- ── Body ────────────────────────────────────────────────────── -->
      <div class="gdrive-notice__body">
        <!-- Warning callout -->
        <div class="gdrive-notice__callout">
          <div class="gdrive-notice__callout-icon-wrap">
            <span class="gdrive-notice__callout-icon" aria-hidden="true">⚠</span>
          </div>
          <div>
            <p class="gdrive-notice__callout-title">You may see a "not verified" warning</p>
            <p class="gdrive-notice__callout-text">
              This is expected. Google's review process is still ongoing, but the integration is
              fully built, tested, and safe to use.
            </p>
          </div>
        </div>

        <!-- Body copy -->
        <p class="gdrive-notice__body-text">
          We only access your data for the purpose of this integration. Reach out to us if you have
          any questions.
        </p>

        <!-- Security badge -->
        <div class="gdrive-notice__security">
          <NIcon size="15" class="gdrive-notice__shield-icon">
            <IconShieldCheck />
          </NIcon>
          <span class="gdrive-notice__security-text">
            Your data is only used for this integration
          </span>
        </div>
      </div>

      <!-- ── Footer ──────────────────────────────────────────────────── -->
      <div class="gdrive-notice__footer">
        <NButton @click="hide">Cancel</NButton>
        <NButton type="primary" @click="handleConfirm">I understand, continue</NButton>
      </div>
    </div>
  </NModal>
</template>

<style scoped>
/* ── Shell ────────────────────────────────────────────────────────────────── */
.gdrive-notice {
  background: var(--n-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.1);
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.gdrive-notice__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 28px 22px;
  border-bottom: 1px solid var(--n-border-color);
  background: linear-gradient(135deg, var(--n-color) 0%, var(--n-color-modal) 100%);
}

.gdrive-notice__logo-wrap {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  background: var(--n-color-modal);
  border: 1px solid var(--n-border-color);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.gdrive-notice__title {
  margin: 0 0 -2px;
  font-size: 16px;
  font-weight: 700;
  color: var(--n-text-color-1);
  letter-spacing: -0.01em;
}

.gdrive-notice__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--n-text-color-3);
}

/* ── Body ─────────────────────────────────────────────────────────────────── */
.gdrive-notice__body {
  padding: 22px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Warning callout */
.gdrive-notice__callout {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 8px;
}

/* Wrapper so the icon top aligns with the title's cap-height */
.gdrive-notice__callout-icon-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 20px; /* matches callout-title line-height */
  margin-top: 1px;
}

.gdrive-notice__callout-icon {
  font-size: 20px;
  line-height: 1;
  color: #d97706;
}

.gdrive-notice__callout-title {
  margin: 0 0 5px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--n-text-color-1);
}

.gdrive-notice__callout-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--n-text-color-2);
}

/* Body paragraph */
.gdrive-notice__body-text {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--n-text-color-2);
}

/* Security badge */
.gdrive-notice__security {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(16, 185, 129, 0.07);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 8px;
}

.gdrive-notice__shield-icon {
  color: #10b981;
  flex-shrink: 0;
}

.gdrive-notice__security-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--n-text-color-2);
}

/* ── Footer ───────────────────────────────────────────────────────────────── */
.gdrive-notice__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 16px 28px 22px;
  border-top: 1px solid var(--n-border-color);
}
</style>
